import { X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Range from "../../../Components/Common/Range/Range";
import { fetchKitchens as fetchPublicKitchens } from "../../../services/catalog.api";
import { FOOD_TYPE } from "../../../services/normalizers";
import "../styles/LeftFilter.css";

const foodTypes = [
  { label: "All",           value: "all",                  color: "#374151", bg: "#f1f5f9" },
  { label: "🔥 Offer",      value: "offer",                color: "#fff",    bg: "#0d6efd" },
  { label: "⚡ Ready to Eat", value: FOOD_TYPE.READY_TO_EAT, color: "#FFD504", bg: "#0d6efd" },
  { label: "☕ Pre-Order",   value: FOOD_TYPE.PRE_ORDER,    color: "#000",    bg: "#f59e0b" },
  { label: "✅ E-Shop",     value: FOOD_TYPE.E_SHOP,       color: "#fff",    bg: "#059669" },
];

const fetchKitchens = () => fetchPublicKitchens({ limit: 100 });

export default function FilterSection({ filter, setFilter, isFilterOpen, setIsFilterOpen }) {
  const handleClearAll = () => {
    setFilter({ foodType: "all", mealType: [], priceMin: 1, priceMax: 5000, kitchens: [], categoryId: "" });
  };

  const { data: kitchens, isPending: isKitchensPending, error } = useQuery({
    queryKey: ["kitchens"],
    queryFn: fetchKitchens,
  });

  if (error) toast.error(error?.message);

  const sidebar = (
    <div className="af-filter-panel">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h5 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#0f172a", fontFamily: "'Inter', sans-serif" }}>
          Filters
        </h5>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            onClick={handleClearAll}
            style={{
              background: "none", border: "none", padding: 0,
              fontSize: 12, fontWeight: 600, color: "#0d6efd",
              cursor: "pointer", fontFamily: "'Inter', sans-serif",
            }}
          >
            Clear all
          </button>
          <button
            className="d-md-none"
            onClick={() => setIsFilterOpen(false)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: "#6b7280" }}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Food Type */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", letterSpacing: 0.8, textTransform: "uppercase", margin: "0 0 10px", fontFamily: "'Inter', sans-serif" }}>
          Food Type
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {foodTypes.map((type) => {
            const isActive = filter.foodType === type.value;
            return (
              <button
                key={type.value}
                onClick={() => setFilter(prev => ({ ...prev, foodType: type.value }))}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 12px", borderRadius: 8,
                  border: isActive ? `1.5px solid ${type.bg}` : "1.5px solid #f1f5f9",
                  background: isActive ? type.bg : "#f9fafb",
                  color: isActive ? type.color : "#374151",
                  fontSize: 13, fontWeight: 600,
                  fontFamily: "'Inter', sans-serif",
                  cursor: "pointer", textAlign: "left",
                  transition: "all 0.15s",
                  width: "100%",
                }}
              >
                {type.label}
                {isActive && (
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: isActive ? "rgba(255,255,255,0.7)" : "transparent", flexShrink: 0 }} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: "#f1f5f9", marginBottom: 24 }} />

      {/* Price Range */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", letterSpacing: 0.8, textTransform: "uppercase", margin: "0 0 10px", fontFamily: "'Inter', sans-serif" }}>
          Price Range (TK)
        </p>
        <Range
          min={filter.priceMin}
          max={filter.priceMax}
          minLimit={1}
          maxLimit={5000}
          label=""
          onChange={(min, max) => setFilter(prev => ({ ...prev, priceMin: min, priceMax: max }))}
        />
      </div>

      {/* Divider */}
      {!isKitchensPending && kitchens?.length > 0 && (
        <div style={{ height: 1, background: "#f1f5f9", marginBottom: 24 }} />
      )}

      {/* Kitchens */}
      {!isKitchensPending && kitchens?.length > 0 && (
        <div>
          <p style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", letterSpacing: 0.8, textTransform: "uppercase", margin: "0 0 10px", fontFamily: "'Inter', sans-serif" }}>
            Kitchens
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
            {kitchens.map((kitchen) => {
              const checked = filter.kitchens.includes(kitchen._id);
              return (
                <label
                  key={kitchen._id}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "7px 10px", borderRadius: 8,
                    border: checked ? "1.5px solid #bfdbfe" : "1.5px solid #f1f5f9",
                    background: checked ? "#eff6ff" : "#f9fafb",
                    cursor: "pointer",
                    fontFamily: "'Inter', sans-serif", fontSize: 13,
                    fontWeight: checked ? 600 : 400, color: checked ? "#1d4ed8" : "#374151",
                    transition: "all 0.15s",
                  }}
                >
                  <input
                    type="checkbox"
                    value={kitchen._id}
                    checked={checked}
                    onChange={(e) => {
                      const updated = e.target.checked
                        ? [...filter.kitchens, kitchen._id]
                        : filter.kitchens.filter(id => id !== kitchen._id);
                      setFilter(prev => ({ ...prev, kitchens: updated }));
                    }}
                    style={{ accentColor: "#0d6efd", width: 15, height: 15, flexShrink: 0 }}
                  />
                  {kitchen.name}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="af-filter-sidebar">
        {sidebar}
      </div>

      {/* Mobile drawer */}
      <div className={`af-filter-drawer ${isFilterOpen ? "open" : ""}`}>
        {sidebar}
      </div>
    </>
  );
}
